import type { TimelineItem } from './collapseAssociationEvents';
import {
  ITEM_TYPES,
  transformToTimelineItems,
} from './collapseAssociationEvents';
import type { TimelineEvent } from './types';
import { EVENT_TYPES } from './types';

export interface TimelineTreeNode {
  item: TimelineItem;
  /** True when this node was synthesized from the lookups event pool to give
   *  causal context: the event is not part of the loaded page (it happened on
   *  another record or an earlier page) but is the direct causal parent of the
   *  children nested beneath it. Rendered de-emphasized. */
  isContext: boolean;
  children: TimelineTreeNode[];
}

const ASSOCIATION_EVENT_TYPES = new Set<string>([
  EVENT_TYPES.ASSOCIATION_ADDED,
  EVENT_TYPES.ASSOCIATION_REMOVED,
]);

const ASSOCIATION_ITEM_TYPES = new Set<string>([
  ITEM_TYPES.ASSOCIATION_ADDED,
  ITEM_TYPES.ASSOCIATION_REMOVED,
  ITEM_TYPES.ASSOCIATION_CHANGED,
]);

/**
 * Mirror-twin suppression: an association event's causal parent is usually the
 * forward twin of the same logical action (the reverse-association cascade
 * copies the forward event's id into the mirror's lineage). Rendering that
 * parent as context would show the same action twice, on every association row.
 * Suppress context synthesis when both sides are association events.
 */
function isMirrorAssociationLineage(
  item: TimelineItem,
  pooledParent: TimelineEvent
): boolean {
  return (
    ASSOCIATION_ITEM_TYPES.has(item.type) &&
    ASSOCIATION_EVENT_TYPES.has(pooledParent.event_type_name)
  );
}

/** Wrap a pooled event in a TimelineItem via the standard transform so the
 *  item registry renders it exactly like a page event of the same type. */
function makeContextNode(event: TimelineEvent): TimelineTreeNode {
  const [item] = transformToTimelineItems([event]);
  return {
    item: item ?? { type: ITEM_TYPES.SINGLE_EVENT, events: [event] },
    isContext: true,
    children: [],
  };
}

/** A pooled event with no `event_type_name` has nothing the item registry can
 *  render as context — treat it as unavailable rather than synthesizing a node. */
function hasRenderableEventType(
  event: TimelineEvent | undefined
): event is TimelineEvent {
  return Boolean(event?.event_type_name);
}

/**
 * Step 7: nest pooled events that are direct children of an on-page item
 * beneath that item as de-emphasized context leaves — the mirror of the
 * upward context roots built in Step 6. Exactly one layer: pooled children
 * are leaves; their own descendants are not fetched or nested.
 */
function attachPooledDescendants(
  items: TimelineItem[],
  nodes: TimelineTreeNode[],
  eventIdToIndex: Map<string, number>,
  contextNodesByParentId: Map<string, TimelineTreeNode>,
  pooledEvents: Record<string, TimelineEvent>
): void {
  for (const pooled of Object.values(pooledEvents)) {
    const ids = pooled.source_event_ids;
    if (!ids || ids.length === 0) {
      continue; // No parent → not a descendant of anything on-page.
    }
    const pooledId = String(pooled.event_id);
    // Skip events actually on the page, or already placed upward as a context
    // parent in Step 6 — contextNodesByParentId is reused here to detect that
    // case; upward placement wins, so never render the same event twice.
    if (eventIdToIndex.has(pooledId) || contextNodesByParentId.has(pooledId)) {
      continue;
    }
    const parentId = String(ids[ids.length - 1]);
    const j = eventIdToIndex.get(parentId);
    if (j === undefined) {
      continue; // Parent NOT on-page → do not surface as a spurious root.
    }
    // Malformed pooled event with no type to render (mirrors Step 3 guard).
    if (!hasRenderableEventType(pooled)) {
      continue;
    }
    const parentEvent = items[j].events.find(
      e => String(e.event_id) === parentId
    );
    if (!parentEvent) {
      continue;
    }
    const childNode = makeContextNode(pooled);
    // Mirror-twin suppression, symmetric to the upward direction: suppress
    // when both the pooled child and its on-page parent are association events.
    if (isMirrorAssociationLineage(childNode.item, parentEvent)) {
      continue;
    }
    nodes[j].children.push(childNode);
  }
}

/**
 * Build the causal tree over collapsed timeline items.
 *
 * Parent resolution: an item's direct parent is the LAST element of its
 * events' `source_event_ids`. When the parent is on the page, the item nests
 * beneath it. When the parent is NOT on the page but is present in the
 * `pooledEvents` lookup (`lookups.events` — one causal layer hydrated by the
 * backend), a de-emphasized CONTEXT node is synthesized from the pooled event
 * and the item nests beneath that; siblings sharing the same off-page parent
 * share one context node. When the parent is unavailable (inaccessible,
 * deleted, or suppressed as an association mirror twin) the item renders as a
 * plain root — off-page causality is never described without showing the
 * actual event. Symmetrically, a pooled event whose direct parent is on the
 * page is attached beneath that on-page item as a de-emphasized context leaf
 * (exactly one layer down; the pooled child's own descendants are not
 * fetched). The same mirror-twin and self/duplicate guards apply, and a
 * pooled child whose parent is not on the page is never surfaced as a root.
 */
export function buildTimelineTree(
  items: TimelineItem[],
  pooledEvents?: Record<string, TimelineEvent>
): TimelineTreeNode[] {
  const n = items.length;

  // Step 1: Build event_id → item index map (covers every event in each group)
  // Normalize to string — event_id may be a number from JSON
  const eventIdToIndex = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    for (const event of items[i].events) {
      eventIdToIndex.set(String(event.event_id), i);
    }
  }

  // Step 2: Compute parent id per item. A grouped item (e.g. an
  // `association_changed` pair) may carry the real upstream parent on a
  // non-representative event, so inspect the direct parent (last of
  // source_event_ids) of EVERY event in the group and prefer a parent that
  // points outside the group. Falling back to the first event's parent keeps
  // single-event behavior unchanged and self/cycle references degrade to a
  // local root downstream.
  const parentIds: (string | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const { events } = items[i];
    // Ids owned by this item — a candidate matching one is a self-reference.
    const ownEventIds = new Set<string>();
    for (const event of events) {
      ownEventIds.add(String(event.event_id));
    }
    // Direct-parent candidates in event order (last of each source_event_ids).
    const candidates: string[] = [];
    for (const event of events) {
      const ids = event.source_event_ids;
      if (ids && ids.length > 0) {
        candidates.push(String(ids[ids.length - 1]));
      }
    }
    // Prefer the first candidate that points outside the group; otherwise fall
    // back to the first candidate (self-reference → local root). No candidate
    // (e.g. an item with no events or no source_event_ids) → genuine root.
    const nonSelf = candidates.find(id => !ownEventIds.has(id));
    parentIds[i] = nonSelf ?? candidates[0] ?? null;
  }

  // Step 3: Resolve parent index (on-page) or a context parent id (pooled).
  const parentIndex: (number | null)[] = new Array(n).fill(null);
  const contextParentIds: (string | null)[] = new Array(n).fill(null);

  for (let i = 0; i < n; i++) {
    const parentId = parentIds[i];
    if (parentId === null) {
      continue; // Genuine root
    }
    const j = eventIdToIndex.get(parentId);
    if (j === undefined) {
      // Parent is not on the loaded page — synthesize context from the pool
      // unless unavailable, malformed (no event type to render), or
      // mirror-twin noise.
      const pooled = pooledEvents?.[parentId];
      if (
        hasRenderableEventType(pooled) &&
        !isMirrorAssociationLineage(items[i], pooled)
      ) {
        contextParentIds[i] = parentId;
      }
    } else if (j !== i) {
      parentIndex[i] = j;
    }
    // j === i: self-reference — treat as local root
  }

  // Step 4: Cycle break — walk up the parentIndex chain from each item;
  // if a node is revisited or the walk exceeds n steps, promote to local root.
  for (let i = 0; i < n; i++) {
    if (parentIndex[i] === null) {
      continue;
    }
    const visited = new Set<number>([i]);
    let cur: number | null = parentIndex[i];
    let steps = 0;
    let hasCycle = false;
    while (cur !== null && steps < n) {
      if (visited.has(cur)) {
        hasCycle = true;
        break;
      }
      visited.add(cur);
      cur = parentIndex[cur];
      steps++;
    }
    if (hasCycle || steps >= n) {
      parentIndex[i] = null;
    }
  }

  // Step 5: Build exactly one node per item
  const nodes: TimelineTreeNode[] = items.map(item => ({
    item,
    isContext: false,
    children: [],
  }));

  // Step 6: Assign children, preserving input order. Items sharing the same
  // off-page parent group under ONE context node, placed at the position of
  // their first (newest) member.
  const roots: TimelineTreeNode[] = [];
  const contextNodesByParentId = new Map<string, TimelineTreeNode>();
  for (let i = 0; i < n; i++) {
    const p = parentIndex[i];
    if (p !== null) {
      nodes[p].children.push(nodes[i]);
      continue;
    }
    const contextParentId = contextParentIds[i];
    if (contextParentId !== null && pooledEvents) {
      let contextNode = contextNodesByParentId.get(contextParentId);
      if (!contextNode) {
        contextNode = makeContextNode(pooledEvents[contextParentId]);
        contextNodesByParentId.set(contextParentId, contextNode);
        roots.push(contextNode);
      }
      contextNode.children.push(nodes[i]);
      continue;
    }
    roots.push(nodes[i]);
  }

  // Step 7: One layer DOWN — nest pooled descendants of on-page items.
  if (pooledEvents) {
    attachPooledDescendants(
      items,
      nodes,
      eventIdToIndex,
      contextNodesByParentId,
      pooledEvents
    );
  }

  return roots;
}
